import { Shield, Lock, Eye, Database, RefreshCw, Mail } from 'lucide-react';

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-950 to-gray-900 text-white py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-14 h-14 bg-indigo-500/20 border border-indigo-400/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Shield className="w-7 h-7 text-indigo-300" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-3">Privacy Policy</h1>
          <p className="text-indigo-300 text-sm">Last updated March 2026 · Effective immediately</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-14 space-y-12">

        {/* Intro */}
        <section>
          <p className="text-gray-700 leading-relaxed">
            FAM ("we," "our," or "us") operates the platform at <strong>fam.autos</strong>.
            This policy explains what information we collect, how we use it, and the
            specific commitments we make around bank verification through Plaid.
          </p>
        </section>

        {/* Plaid — highlighted box */}
        <section className="bg-indigo-50 border border-indigo-200 rounded-2xl p-7">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-indigo-900">Bank Verification via Plaid</h2>
          </div>

          <p className="text-indigo-800 text-sm leading-relaxed mb-5">
            FAM uses <strong>Plaid Technologies, Inc.</strong> solely to verify that you own
            an active bank account. This verification improves your FAM Score and helps
            inform underwriting. Here is exactly what happens — and what does not happen.
          </p>

          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-5 h-5 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-green-800 text-xs font-bold">✓</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-900">What we verify</p>
                <p className="text-sm text-indigo-700 leading-relaxed">
                  That a valid, active bank account exists and belongs to you.
                  A successful connection is sufficient — we record only a boolean pass/fail result.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-700 text-xs font-bold">✗</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-900">What we do NOT collect or store</p>
                <ul className="text-sm text-indigo-700 leading-relaxed mt-1 space-y-1 list-disc list-inside">
                  <li>Account numbers or routing numbers</li>
                  <li>Account balances</li>
                  <li>Transaction history</li>
                  <li>Plaid access tokens or item IDs</li>
                  <li>Bank name, type, or any other account metadata</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-700 text-xs font-bold">→</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-900">How it works technically</p>
                <p className="text-sm text-indigo-700 leading-relaxed">
                  Plaid issues a short-lived <em>public token</em> after you authenticate with your bank.
                  Our server exchanges this token for a Plaid access token,
                  confirms the exchange succeeded, and then <strong>immediately discards the access token</strong>.
                  Nothing from Plaid is written to our database except the word <code className="bg-indigo-100 px-1 rounded text-indigo-900 text-xs">verified</code>
                  — a flag we set ourselves.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-indigo-500 mt-5 pt-4 border-t border-indigo-200">
            Plaid's own privacy policy governs the data Plaid collects directly during the
            connection process.{' '}
            <a
              href="https://plaid.com/legal/#end-user-privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-indigo-700"
            >
              Read Plaid's End-User Privacy Policy
            </a>
            .
          </p>
        </section>

        {/* Information we collect */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-gray-500" />
            <h2 className="text-xl font-bold text-gray-900">Information We Collect</h2>
          </div>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <div>
              <p className="font-semibold text-gray-900 mb-1">Account information</p>
              <p>Name and email address collected at sign-up via Clerk.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">Campaign data</p>
              <p>Campaign title, story, vehicle selections, and financial profile inputs (down payment, max monthly payment) that you provide to build your FAM Score.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">Verification documents</p>
              <p>Bank statements, utility bills, and driver's license images you optionally upload for verification. These are stored in Vercel Blob (encrypted at rest) and linked only to your user ID.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">Social handles</p>
              <p>Social media handles you connect for score verification. We store the handle string only — no OAuth tokens or social account access.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">Payment data</p>
              <p>Payments are processed by Stripe. FAM does not store card numbers or bank account details. Stripe's privacy policy applies to payment data.</p>
            </div>
          </div>
        </section>

        {/* How we use it */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Eye className="w-5 h-5 text-gray-500" />
            <h2 className="text-xl font-bold text-gray-900">How We Use Your Information</h2>
          </div>
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc list-inside">
            <li>To operate and underwrite your campaign</li>
            <li>To calculate your FAM Score and present financing scenarios</li>
            <li>To send transactional emails (campaign updates, supporter notifications)</li>
            <li>To verify your identity for fraud prevention</li>
            <li>To comply with applicable law</li>
          </ul>
          <p className="text-sm text-gray-700 leading-relaxed mt-4">
            We do not sell your personal information. We do not use your data for advertising.
          </p>
        </section>

        {/* Data retention */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <RefreshCw className="w-5 h-5 text-gray-500" />
            <h2 className="text-xl font-bold text-gray-900">Data Retention & Deletion</h2>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            Campaign data and verification records are retained while your account is active
            and for up to 7 years after a funded campaign closes (to satisfy financial
            recordkeeping requirements). You may request deletion of your account and
            associated data at any time by emailing{' '}
            <a href="mailto:support@fam.autos" className="text-indigo-600 hover:underline">
              support@fam.autos
            </a>
            . Requests are processed within 30 days.
          </p>
        </section>

        {/* Contact */}
        <section className="border-t border-gray-100 pt-10">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-gray-500" />
            <h2 className="text-xl font-bold text-gray-900">Contact</h2>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            Questions about this policy or your data?{' '}
            <a href="mailto:support@fam.autos" className="text-indigo-600 hover:underline font-medium">
              support@fam.autos
            </a>
          </p>
          <p className="text-xs text-gray-400 mt-6">
            FAM · fam.autos · This policy may be updated. Continued use after changes
            constitutes acceptance.
          </p>
        </section>

      </div>
    </div>
  );
}
